export type GtfColor = 'blue' | 'orange' | 'red' | 'green' | 'gray' | 'black'

export interface SeriesRenderMeta {
  pane: 'price' | 'own'
  /** fixed y-range for bounded oscillators */
  fixedRange?: [number, number]
  /**
   * levels (pivot points, PSAR dots) hold flat; trends may wiggle; squares
   * render as a run-length row of colored blocks (Echo: the value IS the count
   * of consecutive same-color bars, read right-to-left)
   */
  style: 'trend' | 'level' | 'squares'
  /** semantic GTF color — resolved to theme-appropriate hex at the component layer */
  gtfColor?: GtfColor
  thick?: boolean
}

/**
 * Render-oriented classification owned by the preview. Deliberately NOT the
 * registry's IndicatorType flag: that is a comparability flag for the RHS
 * dropdown and misdeclares ATR, ADX, and CCI as overlays.
 */
const PRICE_PANE = new Set([
  'simpleMovingAverage',
  'exponentialMovingAverage',
  'bollingerBands',
  'keltnerChannel',
  'averageTrueRangeBands',
  'movingAverageEnvelope',
  'donchianChannel',
  'ichimokuCloud',
  'parabolicSAR',
  'pivotPoints',
  'pressure',
])

const FIXED_RANGES: Record<string, [number, number]> = {
  relativeStrengthIndex: [0, 100],
  stochastic: [0, 100],
  williamsR: [-100, 0],
}

const LEVEL_STYLE = new Set(['pivotPoints', 'parabolicSAR'])
const SQUARES_STYLE = new Set(['echo'])

const GTF_META: Record<string, { gtfColor: GtfColor; thick?: boolean }> = {
  thinBlue: { gtfColor: 'blue' },
  thickBlue: { gtfColor: 'blue', thick: true },
  thinOrange: { gtfColor: 'orange' },
  thickOrange: { gtfColor: 'orange', thick: true },
  pressure: { gtfColor: 'green' },
  echo: { gtfColor: 'red' },
  major: { gtfColor: 'red' },
  minor: { gtfColor: 'gray' },
}

const FIELD_COLORS: Record<string, GtfColor> = {
  greenEcho: 'green',
  redEcho: 'red',
  greenMajor: 'green',
  redMajor: 'red',
  grayMinor: 'gray',
  blackMinor: 'black',
  greenPressure: 'green',
  redPressure: 'red',
}

export const getRenderMeta = (indicatorName: string, field: string): SeriesRenderMeta => {
  const meta: SeriesRenderMeta = {
    pane: PRICE_PANE.has(indicatorName) ? 'price' : 'own',
    style: SQUARES_STYLE.has(indicatorName)
      ? 'squares'
      : LEVEL_STYLE.has(indicatorName)
        ? 'level'
        : 'trend',
  }
  const range = FIXED_RANGES[indicatorName]
  if (range) meta.fixedRange = range
  const gtf = GTF_META[indicatorName]
  if (gtf) {
    meta.gtfColor = FIELD_COLORS[field] ?? gtf.gtfColor
    meta.thick = gtf.thick
  }
  return meta
}
